import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";
import { FloatingPortal } from "../Floating/FloatingPortal.jsx";
import { useAnchoredFloatLayout } from "../Floating/useAnchoredFloatLayout.js";
import { onKey } from "../../lib/shortcuts/onKey.js";
import { useEscapeStack } from "../../lib/shortcuts/useEscapeStack.js";
import { useShortcutBindings } from "../../lib/shortcuts/useShortcutBindings.js";
import "./Combobox.css";

function normalizeOption(option) {
  if (typeof option === "string") {
    return {
      label: option,
      value: option
    };
  }

  return option;
}

function matchesOption(option, query) {
  return option.label.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase());
}

function findClosestOption(options, query) {
  const normalizedQuery = query.trim().toLocaleLowerCase();

  if (!normalizedQuery) {
    return options[0] ?? null;
  }

  return options.find((option) => option.value.toLocaleLowerCase() === normalizedQuery)
    ?? options.find((option) => option.label.toLocaleLowerCase() === normalizedQuery)
    ?? options.find((option) => option.label.toLocaleLowerCase().startsWith(normalizedQuery))
    ?? options.find((option) => option.label.toLocaleLowerCase().includes(normalizedQuery))
    ?? null;
}

function getRootFontSize() {
  return Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
}

function getComboboxScrollContainer(anchorElement) {
  return anchorElement.closest(".panel-body, .dashboard-slot.scroll, .fill-area");
}

function getComboboxMenuStyle(menuLayout, maxVisibleOptions) {
  const widthBuffer = 16;

  return {
    left: menuLayout.left === null ? "auto" : `${menuLayout.left}px`,
    right: menuLayout.right === null ? "auto" : `${menuLayout.right}px`,
    minWidth: `${menuLayout.width + widthBuffer}px`,
    width: "max-content",
    maxWidth: "min(calc(100vw - 1rem), 32rem)",
    maxHeight: `min(calc(${maxVisibleOptions} * 2.55rem), ${menuLayout.maxHeight}px)`,
    top: menuLayout.top === null ? "auto" : `${menuLayout.top}px`,
    bottom: menuLayout.bottom === null ? "auto" : `${menuLayout.bottom}px`
  };
}

export function Combobox({
  className = "",
  commitEmptyToFirst = true,
  options = [],
  value = "",
  onChange,
  maxVisibleOptions = 6,
  placeholder = ""
}) {
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const [hasTyped, setHasTyped] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const normalizedOptions = useMemo(() => options.map(normalizeOption), [options]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [ignoreNextBlur, setIgnoreNextBlur] = useState(false);
  const selectedOption = useMemo(
    () => normalizedOptions.find((option) => option.value === value || option.label === value) ?? null,
    [normalizedOptions, value]
  );
  const inputValue = hasTyped ? query : (selectedOption?.label ?? value);

  useEffect(() => {
    if (!hasTyped) {
      setQuery(selectedOption?.label ?? value ?? "");
    }
  }, [hasTyped, selectedOption?.label, value]);

  const filteredOptions = useMemo(() => {
    if (!hasTyped || !query.trim()) {
      return normalizedOptions;
    }

    return normalizedOptions.filter((option) => matchesOption(option, query));
  }, [hasTyped, normalizedOptions, query]);
  const rootFontSize = getRootFontSize();
  const visibleOptionCount = Math.max(1, Math.min(filteredOptions.length, maxVisibleOptions));
  const preferredMenuHeight = visibleOptionCount * (2.55 * rootFontSize);
  const menuLayout = useAnchoredFloatLayout({
    anchorRef: wrapperRef,
    isOpen: isOpen && filteredOptions.length > 0,
    preferredHeight: preferredMenuHeight,
    getScrollContainer: getComboboxScrollContainer
  });

  useEffect(() => {
    if (!hasTyped) {
      const selectedIndex = normalizedOptions.findIndex((option) => option.value === value || option.label === value);
      setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
      return;
    }

    setHighlightedIndex(0);
  }, [hasTyped, normalizedOptions, value]);

  useEscapeStack(() => {
    if (!isOpen) {
      return false;
    }

    setHasTyped(false);
    setIsOpen(false);
    setQuery(selectedOption?.label ?? "");
    inputRef.current?.blur();
    return true;
  }, isOpen);

  useLayoutEffect(() => {
    const menuElement = menuRef.current;

    if (!menuElement || filteredOptions.length === 0) {
      return;
    }

    const activeOption = menuElement.querySelector(".combobox-option.active");
    activeOption?.scrollIntoView({
      block: "nearest"
    });
  }, [filteredOptions.length, highlightedIndex]);

  function commitValue(nextValue) {
    setHasTyped(false);
    setIsOpen(false);
    onChange?.(nextValue);
  }

  function resolveCommittedValue() {
    if (!commitEmptyToFirst && !query.trim()) {
      return selectedOption?.value ?? "";
    }

    const closestOption = findClosestOption(normalizedOptions, query);
    return closestOption?.value ?? "";
  }

  function handleOptionMouseDown(optionValue) {
    setIgnoreNextBlur(true);
    commitValue(optionValue);
  }

  function handleInputBlur() {
    if (ignoreNextBlur) {
      setIgnoreNextBlur(false);
      return;
    }

    commitValue(resolveCommittedValue());
  }

  const comboboxBindings = [
    onKey("ArrowDown", () => {
      if (filteredOptions.length === 0) {
        return;
      }

      setHighlightedIndex((current) => Math.min(current + 1, filteredOptions.length - 1));
    }, { allowInInput: true }),
    onKey("ArrowUp", () => {
      if (filteredOptions.length === 0) {
        return;
      }

      setHighlightedIndex((current) => Math.max(current - 1, 0));
    }, { allowInInput: true }),
    onKey("Enter", () => {
      const highlightedOption = filteredOptions[highlightedIndex];
      commitValue(highlightedOption?.value ?? resolveCommittedValue());
    }, { allowInInput: true })
  ];

  useShortcutBindings(wrapperRef, comboboxBindings);

  return (
    <div class={`combobox${className ? ` ${className}` : ""}`} ref={wrapperRef}>
      <input
        ref={inputRef}
        class="combobox-input"
        type="text"
        placeholder={placeholder}
        value={inputValue}
        onFocus={() => {
          setIsOpen(true);
        }}
        onInput={(event) => {
          setHasTyped(true);
          setIsOpen(true);
          setQuery(event.currentTarget.value);
        }}
        onBlur={handleInputBlur}
      />

      {isOpen && filteredOptions.length > 0 && menuLayout ? (
        <FloatingPortal>
          <div
            class={`combobox-menu combobox-menu--floating${menuLayout.openUpward ? " combobox-menu--upward" : ""}`}
            ref={menuRef}
            style={getComboboxMenuStyle(menuLayout, maxVisibleOptions)}
          >
            {filteredOptions.map((option, index) => (
              <div
                class={`combobox-option${index === highlightedIndex ? " active" : ""}`}
                role="option"
                aria-selected={index === highlightedIndex}
                key={option.value}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleOptionMouseDown(option.value);
                }}
              >
                {option.label}
              </div>
            ))}
          </div>
        </FloatingPortal>
      ) : null}
    </div>
  );
}
